import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useMySubscription } from "@/hooks/useSubscription";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import PlanUsageBanner from "@/components/common/PlanUsageBanner";
import BoostDialog from "@/components/boost/BoostDialog";
import StockSheetDialog from "@/components/myads/StockSheetDialog";
import { formatNZD, timeAgo } from "@/lib/format";
import { buildAdPath } from "@/lib/slugs";
import { toast } from "sonner";
import { Loader2, Trash2, Pause, Play, CheckCheck, Zap, Crown, Pencil, RotateCw, Calendar, Sparkles, Clock, ShieldAlert, Handshake, X, FileText, CheckSquare, Tag } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1607435097405-db48f377bff6?auto=format&fit=crop&w=800&q=60";

export default function MyAdsPage() {
  const qc = useQueryClient();
  const subQ = useMySubscription();
  const q = useQuery({
    queryKey: ["my-ads"],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/listings/mine");
      return data.listings || data || [];
    },
  });

  const statusMut = useMutation({
    mutationFn: async ({ id, status }) => sharedApi.post(`/classifieds/listings/${id}/status`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["my-ads"] }); toast.success("Updated"); },
    onError: (err) => toast.error(err?.response?.data?.detail || "Failed to update"),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => sharedApi.delete(`/classifieds/listings/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["my-ads"] }); toast.success("Deleted"); },
    onError: (err) => toast.error(err?.response?.data?.detail || "Failed to delete"),
  });

  // Iter 756 — ads imported for this vendor by staff / dealer stock sync
  // start hidden (`pending_vendor`) until the vendor approves them here.
  const pendingQ = useQuery({
    queryKey: ["vendor-pending"],
    retry: false,
    queryFn: async () => (await sharedApi.get("/classifieds/vendor-listings/pending")).data,
  });
  const pending = pendingQ.data?.listings || [];
  const pendingTotal = pendingQ.data?.total ?? pending.length;

  const removeFromPending = (id) => {
    qc.setQueryData(["vendor-pending"], (old) =>
      old ? { ...old, total: Math.max(0, (old.total || 1) - 1), listings: (old.listings || []).filter((x) => x.id !== id) } : old,
    );
    qc.invalidateQueries({ queryKey: ["my-ads"] });
  };

  const approveMut = useMutation({
    mutationFn: async (id) => (await sharedApi.post(`/classifieds/vendor-listings/${id}/approve`)).data,
    onSuccess: (_d, id) => { removeFromPending(id); toast.success("Ad approved — it's now live"); },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || "Could not approve this ad");
      qc.invalidateQueries({ queryKey: ["vendor-pending"] });
    },
  });

  const rejectMut = useMutation({
    mutationFn: async ({ id, reason }) =>
      (await sharedApi.post(`/classifieds/vendor-listings/${id}/reject`, reason ? { reason } : {})).data,
    onSuccess: (_d, { id }) => { removeFromPending(id); toast.success("Ad rejected — it stays hidden"); },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || "Could not reject this ad");
      qc.invalidateQueries({ queryKey: ["vendor-pending"] });
    },
  });

  const approveAllMut = useMutation({
    mutationFn: async () => (await sharedApi.post("/classifieds/vendor-listings/approve-all")).data,
    onSuccess: (data) => {
      const n = data?.approved ?? pendingTotal;
      qc.setQueryData(["vendor-pending"], { total: 0, listings: [] });
      qc.invalidateQueries({ queryKey: ["my-ads"] });
      toast.success(`${n} ad${n === 1 ? "" : "s"} now live`);
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Could not approve all"),
  });

  // Iter 783 — multi-select bulk actions on "All my ads".
  // POST /classifieds/listings/bulk acts on own ads only; held ads
  // (pending_review / rejected) are skipped server-side and reported.
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkPriceOpen, setBulkPriceOpen] = useState(false);
  const toggleSelected = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const bulkMut = useMutation({
    mutationFn: async ({ action, price }) =>
      (await sharedApi.post("/classifieds/listings/bulk", {
        ids: [...selected],
        action,
        ...(price != null ? { price } : {}),
      })).data,
    onSuccess: (data) => {
      const skipped = data?.skipped?.length || 0;
      toast.success(
        `${data?.updated ?? 0} ads updated${skipped ? ` · ${skipped} skipped (held for review)` : ""}`,
      );
      setSelected(new Set());
      setBulkPriceOpen(false);
      qc.invalidateQueries({ queryKey: ["my-ads"] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Bulk action failed"),
  });

  const boostMut = useMutation({
    mutationFn: async (id) => sharedApi.post(`/classifieds/listings/${id}/boost`),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["my-ads"] });
      qc.invalidateQueries({ queryKey: ["subscription-me"] });
      const left = res?.data?.boosts_remaining;
      toast.success(`Boosted for 7 days! ${left != null ? `(${left} boost credit${left === 1 ? "" : "s"} left)` : ""}`);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail || "Could not boost ad";
      toast.error(detail, {
        action: { label: "Upgrade", onClick: () => window.location.assign("/plan-and-pricing") },
        duration: 7000,
      });
    },
  });

  const renewMut = useMutation({
    mutationFn: async (id) => sharedApi.post(`/classifieds/listings/${id}/renew`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-ads"] });
      toast.success("Your ad is live for another 3 months 🎉");
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Could not renew this ad"),
  });

  // Paid Boost dialog — spec: SPEC_PAID_BOOST_ALL_PODS
  const [boostFor, setBoostFor] = useState(null);

  // Iter 726 §A — dealer self-upload stock sheet. Entry shows only for
  // eligible accounts (business + verified mobile).
  const [stockSheetOpen, setStockSheetOpen] = useState(false);
  const eligQ = useQuery({
    queryKey: ["stock-sheet-eligibility"],
    retry: false,
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/stock-sheet/eligibility");
      return data;
    },
  });
  const dealerEligible = !!eligQ.data?.eligible;

  // Iter 730 — the stock-sheet reminder notification deep-links here with
  // ?stock_sheet=1; auto-open the dialog once eligibility confirms.
  const [searchParams, setSearchParams] = useSearchParams();

  // Iter 756 — `/my-ads?tab=pending` deep link (vendor e-mail, bell, push).
  const tab = searchParams.get("tab") === "pending" ? "pending" : "all";
  const setTab = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t === "pending") next.set("tab", "pending"); else next.delete("tab");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (searchParams.get("stock_sheet") === "1" && dealerEligible) {
      setStockSheetOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("stock_sheet");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerEligible]);

  const items = q.data || [];
  const usage = subQ.data?.usage || {};
  const plan = subQ.data?.subscription;

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title="My ads" description="Manage your Allsale Classified listings." />
      <Breadcrumbs items={[{ label: "My ads" }]} />

      <PlanUsageBanner className="mt-6" />

      <PartnerHighlightCard />

      <div className="mt-6 mb-4 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">My ads</h1>
          <p className="mt-2 text-slate-500">{items.length} listing{items.length === 1 ? "" : "s"} · manage & update</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {plan && (
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-soft px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-card" data-testid="my-ads-plan-badge">
              <Crown className="w-3.5 h-3.5 text-[color:var(--brand-orange)]" />
              {plan.plan_name || plan.plan_slug || "Free"} plan
              {usage.listing_limit != null && (
                <span className="text-slate-400 font-medium">· {usage.active_listings ?? 0}/{usage.listing_limit} ads</span>
              )}
              {usage.boosts_remaining != null && (
                <span className="text-slate-400 font-medium">· {usage.boosts_remaining} boost{usage.boosts_remaining === 1 ? "" : "s"}</span>
              )}
            </div>
          )}
          <Link to="/plan-and-pricing" className="text-xs font-semibold text-[color:var(--brand-blue)] hover:underline" data-testid="my-ads-upgrade">
            Upgrade
          </Link>
          <Link
            to="/my-ads/feedback"
            data-testid="my-ads-feedback-link"
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-[color:var(--brand-blue)] rounded-full border border-soft bg-white px-3 py-1.5 transition-colors"
          >
            Feedback inbox
          </Link>
          {dealerEligible && (
            <button
              type="button"
              onClick={() => setStockSheetOpen(true)}
              data-testid="stock-sheet-open-btn"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[color:var(--brand-blue)] rounded-full border border-[color:var(--brand-blue)]/50 hover:border-[color:var(--brand-blue)] bg-white px-3 py-1.5 transition-colors"
            >
              <FileText className="w-3.5 h-3.5" /> Update from stock sheet
            </button>
          )}
          <Link to="/post-ad" className="rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-5 py-2.5 transition-colors">
            + New ad
          </Link>
        </div>
      </div>

      {/* Iter 756 — Pending approval / All my ads segments */}
      <div className="mb-5 inline-flex p-1 rounded-2xl bg-slate-100 gap-1" role="tablist" aria-label="My ads tabs">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "pending"}
          data-testid="myads-tab-pending"
          onClick={() => setTab("pending")}
          className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${tab === "pending" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
        >
          Pending approval
          {pendingTotal > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 rounded-full bg-purple-600 text-white text-[11px] font-bold px-1.5">
              {pendingTotal}
            </span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "all"}
          data-testid="myads-tab-all"
          onClick={() => setTab("all")}
          className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${tab === "all" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
        >
          All my ads ({items.length})
        </button>
      </div>

      {tab === "pending" && (
        <div className="mb-8">
          {pendingTotal > 0 && (
            <div className="mb-4 flex items-center justify-between gap-3">
              <p className="text-sm text-slate-600">
                These ads were imported for you — they stay hidden until you approve them.
              </p>
              <button
                type="button"
                data-testid="myads-approve-all"
                disabled={approveAllMut.isPending}
                onClick={() => {
                  if (window.confirm(`Approve all ${pendingTotal} pending ads and make them live?`)) {
                    approveAllMut.mutate();
                  }
                }}
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-bold px-4 py-2 transition-colors shrink-0"
              >
                {approveAllMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCheck className="w-4 h-4" />}
                Approve all
              </button>
            </div>
          )}
          {pendingQ.isLoading && (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}
          {!pendingQ.isLoading && pendingTotal === 0 && (
            <div className="rounded-2xl border border-dashed border-soft bg-white p-12 text-center" data-testid="myads-empty">
              <Clock className="w-8 h-8 mx-auto text-slate-300" />
              <p className="mt-3 text-sm text-slate-500 max-w-md mx-auto">
                Nothing waiting for you — ads our team or your website sync imports for you appear here until you approve them.
              </p>
            </div>
          )}
          <div className="space-y-4">
            {pending.map((l) => (
              <PendingVendorCard
                key={l.id}
                listing={l}
                onApprove={() => approveMut.mutate(l.id)}
                onReject={(reason) => rejectMut.mutate({ id: l.id, reason })}
                busy={approveMut.isPending || rejectMut.isPending}
              />
            ))}
          </div>
        </div>
      )}

      {tab === "all" && (
      <>
      {q.isLoading && (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}

      {/* Iter 726 §A — dashed stock-sheet card for dealers with no ads */}
      {!q.isLoading && items.length === 0 && dealerEligible && (
        <button
          type="button"
          onClick={() => setStockSheetOpen(true)}
          data-testid="stock-sheet-empty-card"
          className="mb-4 w-full rounded-2xl border-2 border-dashed border-[color:var(--brand-blue)]/40 hover:border-[color:var(--brand-blue)] bg-white p-6 text-left flex items-start gap-4 transition-colors"
        >
          <div className="w-11 h-11 rounded-xl bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] grid place-items-center shrink-0">
            <FileText className="w-5 h-5" />
          </div>
          <span>
            <span className="block font-display font-bold text-slate-900">
              Got a stock list PDF? Upload it and we&apos;ll create your ads.
            </span>
            <span className="block text-sm text-slate-500 mt-0.5">
              One ad per vehicle · new ads go live after a quick review.
            </span>
          </span>
        </button>
      )}

      {!q.isLoading && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-soft bg-white p-14 text-center">
          <h3 className="font-display font-bold text-lg text-slate-800">No ads yet</h3>
          <p className="text-sm text-slate-500 mt-1">Post your first ad — it&apos;s free.</p>
          <Link to="/post-ad" className="mt-6 inline-block rounded-full bg-[color:var(--brand-blue)] text-white font-bold px-5 py-2.5">
            Post an ad
          </Link>
        </div>
      )}

      {/* Iter 783 — select toggle for bulk actions */}
      {items.length > 0 && (
        <div className="mb-3 flex items-center justify-between gap-3">
          <button
            type="button"
            data-testid="myads-select-toggle"
            onClick={() => { setSelectMode((v) => !v); setSelected(new Set()); }}
            className={`chip ${selectMode ? "border-[color:var(--brand-blue)] text-[color:var(--brand-blue)] font-bold" : ""}`}
          >
            <CheckSquare className="w-3.5 h-3.5" /> {selectMode ? "Done" : "Select ads"}
          </button>
          {selectMode && (
            <button
              type="button"
              data-testid="myads-select-all"
              onClick={() =>
                setSelected((prev) => (prev.size === items.length ? new Set() : new Set(items.map((x) => x.id))))
              }
              className="text-xs font-bold text-[color:var(--brand-blue)]"
            >
              {selected.size === items.length ? "Clear all" : `Select all (${items.length})`}
            </button>
          )}
        </div>
      )}

      <div className="space-y-4">
        {items.map((l) => (
          <div
            key={l.id}
            className={`relative rounded-2xl bg-white shadow-card overflow-hidden ${selectMode && selected.has(l.id) ? "ring-2 ring-[color:var(--brand-blue)]" : ""}`}
          >
            {selectMode && (
              <label className="absolute top-3 left-3 z-10 w-8 h-8 rounded-full bg-white shadow-card grid place-items-center cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`myads-select-${l.id}`}
                  checked={selected.has(l.id)}
                  onChange={() => toggleSelected(l.id)}
                  className="w-[18px] h-[18px] accent-[color:var(--brand-blue)]"
                />
              </label>
            )}
            {l.status === "pending_review" && (
              <div
                data-testid={`my-ads-banner-pending-${l.id}`}
                className="flex items-start gap-2.5 px-4 py-3 bg-amber-50 border-b border-amber-200 text-amber-900"
              >
                <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                <p className="text-sm">
                  Your listing <span className="font-semibold">&ldquo;{l.title}&rdquo;</span> is under review
                  before it goes live. This usually takes a few hours. We&apos;ll notify you once it&apos;s
                  approved.
                </p>
              </div>
            )}
            {l.status === "rejected" && (
              <div
                data-testid={`my-ads-banner-rejected-${l.id}`}
                className="flex items-start gap-2.5 px-4 py-3 bg-red-50 border-b border-red-200 text-red-900"
              >
                <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                <p className="text-sm">
                  Your listing <span className="font-semibold">&ldquo;{l.title}&rdquo;</span> was not approved
                  for public listing.
                  {l.rejected_reason ? <> Reason: {l.rejected_reason}.</> : null} Contact support if you
                  have questions.
                </p>
              </div>
            )}
            <div className="p-4 flex flex-col sm:flex-row gap-4 items-start">
            <Link to={buildAdPath(l)} className="shrink-0">
              <img src={l.thumb || l.images?.[0] || FALLBACK} alt="" loading="lazy" className="w-28 h-28 rounded-xl object-cover" />
            </Link>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Link to={buildAdPath(l)} className="font-display font-bold text-lg text-slate-900 hover:text-[color:var(--brand-blue)] transition-colors clamp-2">
                    {l.title}
                  </Link>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {timeAgo(l.created_at)} · {l.views || 0} views · <span className={`font-semibold ${statusColor(l.status)}`}>{statusLabel(l.status)}</span>
                    {(() => {
                      const info = expiryInfo(l);
                      if (!info) return null;
                      return (
                        <span
                          className={`ml-1 inline-flex items-center gap-1 font-semibold ${info.tone}`}
                          data-testid={`my-ads-expires-${l.id}`}
                        >
                          · <Calendar className="w-3 h-3" /> {info.label}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <div className="text-lg font-display font-black text-[color:var(--brand-blue)] whitespace-nowrap">{formatNZD(l.price)}</div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {(() => {
                  const info = expiryInfo(l);
                  if (!info?.showRenew) return null;
                  return (
                    <button
                      onClick={() => renewMut.mutate(l.id)}
                      disabled={renewMut.isPending}
                      className="chip bg-[color:var(--brand-orange)]/10 border-[color:var(--brand-orange)]/60 text-[color:var(--brand-orange-hover)] hover:border-[color:var(--brand-orange)] font-bold disabled:opacity-60"
                      data-testid={`my-ads-renew-${l.id}`}
                      title="Renew for 3 months"
                    >
                      <RotateCw className="w-3.5 h-3.5" />
                      Keep it live — renew for 3 months
                    </button>
                  );
                })()}
                <button
                  onClick={() => boostMut.mutate(l.id)}
                  disabled={boostMut.isPending || l.is_featured}
                  className="chip text-[color:var(--brand-orange)] border-[color:var(--brand-orange)]/40 hover:border-[color:var(--brand-orange)] disabled:opacity-50"
                  data-testid={`my-ads-boost-${l.id}`}
                  title={l.is_featured ? "Already boosted" : `Boost this ad for 7 days${usage.boosts_remaining != null ? ` (${usage.boosts_remaining} left)` : ""}`}
                >
                  <Zap className="w-3.5 h-3.5" />
                  {l.is_featured ? "Boosted" : `Boost${usage.boosts_remaining != null ? ` (${usage.boosts_remaining})` : ""}`}
                </button>
                {l.status === "active" && (
                  <button
                    onClick={() => setBoostFor(l)}
                    className="chip text-[color:var(--brand-blue)] border-[color:var(--brand-blue)]/40 hover:border-[color:var(--brand-blue)]"
                    data-testid={`my-ads-paid-boost-${l.id}`}
                    title="Pay to feature this ad on the homepage for 7 / 14 / 30 days"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    {l.is_featured ? "Feature longer — from $5" : "Feature on homepage"}
                  </button>
                )}
                {l.status !== "sold" && (
                  <Link
                    to={`/edit-ad/${l.id}`}
                    data-testid={`my-ads-edit-${l.id}`}
                    className="chip"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </Link>
                )}
                {l.status !== "sold" && (
                  <button
                    onClick={() => statusMut.mutate({ id: l.id, status: "sold" })}
                    className="chip"
                  >
                    <CheckCheck className="w-3.5 h-3.5" /> Mark sold
                  </button>
                )}
                {l.status === "active" && (
                  <button
                    onClick={() => statusMut.mutate({ id: l.id, status: "inactive" })}
                    className="chip"
                  >
                    <Pause className="w-3.5 h-3.5" /> Pause
                  </button>
                )}
                {l.status === "inactive" && (
                  <button
                    onClick={() => statusMut.mutate({ id: l.id, status: "active" })}
                    className="chip"
                  >
                    <Play className="w-3.5 h-3.5" /> Activate
                  </button>
                )}
                <button
                  onClick={() => { if (window.confirm("Delete this ad?")) deleteMut.mutate(l.id); }}
                  className="chip text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
            </div>
          </div>
        ))}
      </div>
      {selectMode && selected.size > 0 && <div className="h-20" aria-hidden="true" />}
      </>
      )}

      {/* Iter 783 — sticky bulk-action bar */}
      {tab === "all" && selectMode && selected.size > 0 && (
        <div
          data-testid="myads-bulk-bar"
          className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-soft shadow-[0_-6px_24px_rgba(2,6,23,0.10)]"
        >
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-2 flex-wrap">
            <span className="text-sm font-black text-slate-900 mr-1" data-testid="myads-bulk-count">
              {selected.size} selected
            </span>
            <button
              type="button"
              data-testid="myads-bulk-sold"
              disabled={bulkMut.isPending}
              onClick={() => bulkMut.mutate({ action: "sold" })}
              className="chip font-bold"
            >
              <Handshake className="w-3.5 h-3.5" /> Mark sold
            </button>
            <button
              type="button"
              data-testid="myads-bulk-pause"
              disabled={bulkMut.isPending}
              onClick={() => bulkMut.mutate({ action: "deactivate" })}
              className="chip font-bold"
            >
              <Pause className="w-3.5 h-3.5" /> Pause
            </button>
            <button
              type="button"
              data-testid="myads-bulk-reactivate"
              disabled={bulkMut.isPending}
              onClick={() => bulkMut.mutate({ action: "reactivate" })}
              className="chip font-bold text-emerald-700"
            >
              <Play className="w-3.5 h-3.5" /> Reactivate
            </button>
            <button
              type="button"
              data-testid="myads-bulk-price"
              disabled={bulkMut.isPending}
              onClick={() => setBulkPriceOpen(true)}
              className="chip font-bold text-[color:var(--brand-blue)]"
            >
              <Tag className="w-3.5 h-3.5" /> Change price
            </button>
            <button
              type="button"
              data-testid="myads-bulk-delete"
              disabled={bulkMut.isPending}
              onClick={() => {
                if (window.confirm(`Delete ${selected.size} selected ad${selected.size === 1 ? "" : "s"}? This can't be undone.`)) {
                  bulkMut.mutate({ action: "delete" });
                }
              }}
              className="chip font-bold text-red-600 hover:border-red-300"
            >
              {bulkMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Delete
            </button>
          </div>
        </div>
      )}

      {bulkPriceOpen && (
        <BulkPriceModal
          count={selected.size}
          busy={bulkMut.isPending}
          onApply={(price) => bulkMut.mutate({ action: "price", price })}
          onClose={() => setBulkPriceOpen(false)}
        />
      )}

      <style>{`.chip{display:inline-flex;align-items:center;gap:.35rem;font-size:.75rem;font-weight:600;padding:.4rem .8rem;border-radius:9999px;border:1px solid var(--border-soft);color:#334155;background:#fff;transition:background .2s,border-color .2s;}
      .chip:hover{border-color:var(--brand-blue);}`}</style>

      <BoostDialog
        open={!!boostFor}
        onOpenChange={(v) => { if (!v) setBoostFor(null); }}
        listing={boostFor || { id: "", title: "" }}
      />

      <StockSheetDialog open={stockSheetOpen} onClose={() => setStockSheetOpen(false)} />
    </div>
  );
}

/** Iter 783 — one NZD price applied to every selected ad. */
function BulkPriceModal({ count, busy, onApply, onClose }) {
  const [value, setValue] = useState("");
  const price = Number(value);
  const valid = value !== "" && Number.isFinite(price) && price >= 0;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-sm rounded-2xl bg-white shadow-xl p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="myads-bulk-price-modal"
      >
        <h3 className="font-display font-bold text-lg text-slate-900">Change price</h3>
        <p className="mt-1 text-sm text-slate-500">
          One price for all {count} selected ad{count === 1 ? "" : "s"}.
        </p>
        <form
          className="mt-4"
          onSubmit={(e) => { e.preventDefault(); if (valid && !busy) onApply(price); }}
        >
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
            <input
              autoFocus
              inputMode="decimal"
              data-testid="myads-bulk-price-input"
              value={value}
              onChange={(e) => setValue(e.target.value.replace(/[^0-9.]/g, "").slice(0, 10))}
              placeholder="0.00"
              className="w-full pl-7 pr-3 py-2.5 rounded-xl bg-slate-50 border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm font-semibold"
            />
          </div>
          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-soft bg-white text-slate-600 text-sm font-semibold px-4 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!valid || busy}
              data-testid="myads-bulk-price-apply"
              className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] disabled:opacity-50 text-white text-sm font-bold px-5 py-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Apply
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function statusColor(status) {
  if (status === "active") return "text-emerald-600";
  if (status === "sold") return "text-slate-500";
  if (status === "expired") return "text-red-500";
  if (status === "pending_vendor") return "text-purple-600";
  if (status === "rejected_by_vendor") return "text-slate-400";
  return "text-amber-600";
}

/** Iter 756 — human labels for the vendor-approval statuses. */
function statusLabel(status) {
  if (status === "pending_vendor") return "Awaiting your approval";
  if (status === "rejected_by_vendor") return "Rejected by you";
  return status;
}

/** Iter 756 — one imported ad awaiting the vendor's decision. */
function PendingVendorCard({ listing: l, onApprove, onReject, busy }) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  let sourceHost = "";
  try { sourceHost = l.source_url ? new URL(l.source_url).hostname.replace(/^www\./, "") : ""; } catch { /* ignore */ }
  return (
    <div data-testid={`myad-pending-${l.id}`} className="rounded-2xl bg-white shadow-card overflow-hidden">
      <div className="p-4 flex flex-col sm:flex-row gap-4 items-start">
        <img
          src={l.thumb || l.images?.[0] || FALLBACK}
          alt=""
          loading="lazy"
          onError={(e) => { e.currentTarget.src = FALLBACK; }}
          className="w-28 h-28 rounded-xl object-cover shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-display font-bold text-lg text-slate-900 clamp-2">{l.title}</div>
              {sourceHost && (
                <div className="text-xs text-slate-400 mt-0.5" data-testid={`myad-pending-source-${l.id}`}>
                  Imported from {sourceHost}
                </div>
              )}
            </div>
            <div className="text-lg font-display font-black text-[color:var(--brand-blue)] whitespace-nowrap">{formatNZD(l.price)}</div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid={`myad-approve-${l.id}`}
              disabled={busy}
              onClick={onApprove}
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-bold px-4 py-2 transition-colors"
            >
              <CheckCheck className="w-4 h-4" /> Approve &amp; go live
            </button>
            <button
              type="button"
              data-testid={`myad-reject-${l.id}`}
              disabled={busy}
              onClick={() => setRejectOpen((v) => !v)}
              className="chip text-red-600 hover:text-red-700"
            >
              <X className="w-3.5 h-3.5" /> Reject
            </button>
          </div>
          {rejectOpen && (
            <div className="mt-3 rounded-xl border border-red-100 bg-red-50/60 p-3" data-testid={`myad-reject-panel-${l.id}`}>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                Why are you rejecting this ad? (optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 500))}
                rows={2}
                maxLength={500}
                data-testid={`myad-reject-reason-${l.id}`}
                placeholder="e.g. already sold, wrong price…"
                className="w-full rounded-lg border border-soft bg-white px-3 py-2 text-sm outline-none focus:border-red-300"
              />
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  data-testid={`myad-reject-confirm-${l.id}`}
                  disabled={busy}
                  onClick={() => onReject(reason.trim() || undefined)}
                  className="rounded-full bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-xs font-bold px-4 py-2 transition-colors"
                >
                  Confirm reject
                </button>
                <button
                  type="button"
                  onClick={() => { setRejectOpen(false); setReason(""); }}
                  className="rounded-full border border-soft bg-white text-slate-600 text-xs font-semibold px-4 py-2"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Renewal UI helper (spec §2.2 + "Ends today!" badge):
 * - Show `expires <d MMM>` on every non-sold ad.
 * - Show renew button when expires_at ≤ 7 days away OR status is `expired`.
 * - When < 24h left and not yet expired → red "Ends today!" badge (isFinal=true).
 */
function expiryInfo(l) {
  if (!l || l.status === "sold" || l.status === "deleted") return null;
  const iso = l.expires_at;
  if (!iso) return null;
  const exp = new Date(iso);
  if (Number.isNaN(exp.getTime())) return null;
  const now = new Date();
  const msLeft = exp.getTime() - now.getTime();
  const daysLeft = msLeft / (1000 * 60 * 60 * 24);
  const isFinal = l.status !== "expired" && daysLeft > 0 && daysLeft < 1;
  const label =
    l.status === "expired"
      ? "Expired"
      : isFinal
      ? "Ends today!"
      : `Expires ${exp.toLocaleDateString("en-NZ", { day: "numeric", month: "short" })}`;
  const tone =
    l.status === "expired" || isFinal
      ? "text-red-600"
      : daysLeft <= 7
      ? "text-amber-600"
      : "text-slate-400";
  const showRenew = l.status === "expired" || daysLeft <= 7;
  return { label, tone, showRenew, daysLeft, isFinal };
}

// Iter-290 §1d — dismissible "Become a Partner" card on My Ads.
// Hidden for existing vendors (GET /engagement/vendors/me returns 200)
// and once the user has dismissed it in this browser.
const PARTNER_DISMISS_KEY = "allsale_myads_partner_dismissed_v1";

function PartnerHighlightCard() {
  const [dismissed, setDismissed] = useState(
    () => (typeof window !== "undefined" && localStorage.getItem(PARTNER_DISMISS_KEY) === "1"),
  );
  const meQ = useQuery({
    queryKey: ["vendor-me"],
    retry: false,
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/engagement/vendors/me", {
        headers: { "X-App-Platform": "nz_classified" },
        params: { platform: "nz_classified" },
      });
      return data;
    },
  });
  // Existing vendor → hide (backend 200 = vendor record exists).
  if (meQ.data?.vendor_id || meQ.data?.business_name) return null;
  if (dismissed) return null;
  return (
    <div
      data-testid="my-ads-partner-card"
      className="mt-4 rounded-2xl bg-gradient-to-r from-slate-900 to-[#0F70A8] text-white p-5 md:p-6 flex items-start gap-4"
    >
      <div className="w-11 h-11 rounded-xl bg-white/10 grid place-items-center shrink-0">
        <Handshake className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-black uppercase tracking-widest text-white/70">Partner Program</div>
        <div className="mt-0.5 font-display text-lg md:text-xl font-black leading-tight">
          Own a store? Get a branded scan page + instant Stripe payouts.
        </div>
        <div className="mt-3">
          <Link
            to="/partner-program"
            data-testid="my-ads-partner-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold text-sm px-4 py-2 transition-colors"
          >
            Become a partner →
          </Link>
        </div>
      </div>
      <button
        type="button"
        onClick={() => {
          localStorage.setItem(PARTNER_DISMISS_KEY, "1");
          setDismissed(true);
        }}
        aria-label="Dismiss"
        data-testid="my-ads-partner-dismiss"
        className="w-8 h-8 rounded-full grid place-items-center text-white/70 hover:bg-white/10"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

